import React from 'react';
import styled from 'styled-components';
import { forDesktop } from '@westwing/ui-kit/breakPoints';
import { header1, body2 } from '@westwing/ui-kit/typography';
import { useLooksTracking } from '../hooks/useLooksTracking';
import { useBellaSelector } from 'Client/redux/hooks';
import { NoLooksFoundData } from 'Client/redux/looksPage/types';
import { LazyHybridImage } from 'Client/components/Generic/HybridImage';
import { useFetchingAction } from 'Client/hooks/useFetchingAction/useFetchingAction';
import { fetchNoLooksFoundIfNeeded } from 'Client/redux/looksPage/actions';
import useRouter from 'Client/hooks/useRouter';
import { TrackLLPImpressionEvents, TrackLooksActions } from 'Common/ga/types';

const StyledNotFound = styled.div`
    position: relative;
`;

const StyledTextContainer = styled.div`
    position: absolute;
    width: 100%;
    text-align: center;
    margin-top: 29px;

    ${forDesktop`
        margin-top: 49px;
    `}
`;

const StyledTitle = styled.h2`
    ${header1}
    margin: 0;
    color: ${({ theme }) => theme.colors.charcoal100};
    padding-top: 6px;
`;

const StyledSubTitle = styled.span`
    ${body2}
    display: inline-block;
    color: ${({ theme }) => theme.colors.charcoal100};
`;

const LooksNotFound: React.FC = () => {
    const {
        location: { pathname },
    } = useRouter();
    const { LLP: LLPTracking } = useLooksTracking();
    const looksTotal = useBellaSelector<number>(state => state.looksPage.looks.total || 0);
    const isFetching = useBellaSelector<boolean>(state => state.looksPage.isFetching);
    const noLooksFoundData = useBellaSelector<NoLooksFoundData>(state => state.looksPage.noLooksFound);

    useFetchingAction(fetchNoLooksFoundIfNeeded());

    if (isFetching || looksTotal > 0) {
        return null;
    }

    const handleTrackImpressionFunction = () =>
        LLPTracking.trackImpression(TrackLLPImpressionEvents.NoLooksFound, TrackLooksActions.LLPNoLooksFound, pathname);

    return (
        <StyledNotFound data-testid="no-looks-found">
            <StyledTextContainer>
                <StyledTitle>{noLooksFoundData.title}</StyledTitle>
                <StyledSubTitle>{noLooksFoundData.subtitle}</StyledSubTitle>
            </StyledTextContainer>
            <LazyHybridImage
                alt={noLooksFoundData.subtitle}
                mobileSrc={noLooksFoundData.imageMobile?.file.url || ''}
                desktopSrc={noLooksFoundData.imageDesktop?.file.url || ''}
                fallbackSrc={noLooksFoundData.imageDesktop?.file.url}
                trackImpressionFunction={handleTrackImpressionFunction}
            />
        </StyledNotFound>
    );
};

export default LooksNotFound;
